import React, { useState, useRef, useEffect } from 'react';
import { Input } from "@sourcepilot/ui/framework/ui/atoms/input";
import { Textarea } from "@sourcepilot/ui/framework/ui/atoms/textarea";
import { Button } from "@sourcepilot/ui/framework/ui/atoms/button";
import { Edit2, Trash2, MessageSquareShare, Save, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@sourcepilot/ui/framework/ui/atoms/select";
import { AsyncSelect } from '@sourcepilot/ui/framework/ui/molecules/AsyncSelect'; // Import from local file
import { useAddToContextButton } from "@sourcepilot/ui/framework/ui/views/AddToContextStore";

interface InPlaceEditableProps {
  children: React.ReactNode;
  onSave?: (value: string | string[]) => void;
  onDelete?: (e: React.MouseEvent) => void;
  onChat?: (index?: number) => void;
  /** When set (and the app has registered an add-to-context button via
   *  `setAddToContextButton`), the chat affordance renders that shared
   *  button bound to this node — superseding `onChat`. Lets callers drop
   *  their bespoke `handleAddToContext` and the node→context-item mapping. */
  chatNode?: any;
  editable?: boolean; // Changed to boolean
  chattable?: boolean;
  deletable?: boolean;
  inputType?: ('input' | 'textarea' | 'select' | 'autocomplete') | ('input' | 'textarea' | 'select' | 'autocomplete')[]; // Added autocomplete
  className?: string;
  defaultOpen?: boolean;
  placeholder?: string;
  iconPosition?: 'right' | 'bottom';
  textAreaRows?: number;
  maxHeight?: number;
  values?: any[];
  index?: number;
  requirementTypes?: { type: string, description: string, examples: string[] }[]; // Added types for requirementTypes
  loadOptions?: (value: string) => Promise<any[]>;
}

export function InPlaceEditable({
  children,
  onSave = () => {},
  onDelete,
  onChat,
  chatNode,
  editable = false,
  chattable = true,
  deletable = true,
  inputType = 'input',
  className = '',
  defaultOpen = false,
  placeholder = '',
  iconPosition = 'right',
  textAreaRows = 1,
  maxHeight = 220,
  values,
  requirementTypes,
  loadOptions,
}: InPlaceEditableProps) {
  // App-injected add-to-context button (null when none registered). When a
  // `chatNode` is supplied and a button is registered, it supersedes the
  // legacy `onChat` affordance below.
  const InjectedChatButton = useAddToContextButton();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(Array.isArray(values) ? values : [children?.toString() || '']);
  const [hasSavedContent, setHasSavedContent] = useState(values?.[0]);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Set the height based on the current content when entering edit mode
      const textArea = inputRef.current as HTMLTextAreaElement;
      textArea.style.height = 'auto'; // Reset height
      textArea.style.height = `${Math.min(textArea.scrollHeight, maxHeight)}px`; // Set height to scrollHeight
    }
  }, [isEditing]);

  const handleEdit = () => {
    setValue(Array.isArray(values) ? values : [children?.toString() || '']);
    setIsEditing(true);
  };

  const handleSave = () => {
    onSave(value);
    setHasSavedContent(!!value);
    setIsEditing(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    if (onDelete) {
      onDelete(e);
    }
  };

  const handleChat = () => {
    if (onChat) {
      onChat();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputType !== 'textarea') { // Check first input type
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      // the edit takes the key: an Esc in a chat must not also stop its turn
      e.preventDefault();
      setIsEditing(false);
      setValue(Array.isArray(values) ? values : [children?.toString() || '']);
    }
  };

  const inputTypes = Array.isArray(inputType) ? inputType : [inputType]; // Ensure inputType is always an array

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>, fieldIndex: number) => {
    const newValue = e.target.value;
    setValue(prev => {
      const updated = [...prev];
      updated[fieldIndex] = newValue;
      return updated;
    });
    // Dynamically adjust the rows based on content
    const textArea = e.target;
    textArea.style.height = 'auto'; // Reset height
    textArea.style.height = `${Math.min(textArea.scrollHeight, maxHeight)}px`; // Set height to scrollHeight
  };

  const InputComponent = (fieldIndex: number) => {
    const type = inputTypes[fieldIndex] || 'input'; // Access input type from array
    const val = Array.isArray(value) ? value[fieldIndex] : value; // Access value based on inputType

    if (type === 'select') {
      return (
        <Select value={val} onValueChange={(v) => setValue(prev => {
          const updated = [...prev];
          updated[fieldIndex] = v;
          return updated;
        })}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select requirement type" />
          </SelectTrigger>
          <SelectContent>
            {requirementTypes?.map((type) => (
              <SelectItem key={type.type} value={type.type}>
                {type.type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    } else if (type === 'textarea') {
      return (
        <Textarea
          ref={inputRef as any}
          value={val}
          onChange={(e) => handleChange(e, fieldIndex)}
          onKeyDown={handleKeyDown}
          className="w-full resize-none"
          placeholder={placeholder}
          rows={textAreaRows}
        />
      );
    } else if (type === 'autocomplete') {
      return (
        <AsyncSelect
          value={val}
          loadOptions={loadOptions as (inputValue: string) => Promise<any[]>}
          onChange={(newValue) => setValue(newValue)}
          className="w-full"
          placeholder={placeholder}
        />
      );
    } else {
      return (
        <Input
          ref={inputRef as any}
          value={val?.toString() || ''} // Convert value to string, handle null
          onChange={(e) => setValue(prev => {
            const updated = [...prev];
            updated[fieldIndex] = e.target.value;
            return updated;
          })}
          onKeyDown={handleKeyDown}
          className="w-full"
          placeholder={placeholder}
        />
      );
    }
  };

  const iconContainerClassName =
    iconPosition === 'right'
      ? "flex space-x-0 absolute top-0 right-1"
      : "flex space-x-0 absolute top-full mt-1 right-0";

  return (
    <div className={`group relative ${className}`}>
      {isEditing || defaultOpen ? (
        <div className="relative">
          <div className="flex space-x-2">
            {inputTypes.map((type, fieldIndex) => ( // Use inputTypes for mapping
              <div key={fieldIndex} className={type === 'select' ? 'w-48' : 'flex-1'}>
                {InputComponent(fieldIndex)}
              </div>
            ))}
          </div>
          <div className={iconContainerClassName}>
            {(value.length > 0 && (value[0] || typeof value[0] === 'object')) && ( // Check if there's any content to save, including objects from AsyncSelect
              <Button variant="ghost" size="sm" className="p-0.5" onClick={(e) => { e.stopPropagation(); handleSave(); }}>
                <Save className="h-4 w-6" />
              </Button>
            )}
            {hasSavedContent && (
              <Button variant="ghost" size="sm" className="p-0.5" onClick={(e) => { e.stopPropagation(); setIsEditing(false); setValue(Array.isArray(values) ? values : [children?.toString() || '']); }}>
                <X className="h-4 w-6" />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="relative flex items-center w-full">
          <div className={`flex-1`}>
            {children}
          </div>

          <div className={iconContainerClassName}>
            {editable && ( // Use the boolean editable prop directly
              <Button variant="ghost" size="sm" className="p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); handleEdit(); }}>
                <Edit2 className="h-4 w-6" />
              </Button>
            )}
            {deletable && onDelete && ( // Use the boolean deletable prop directly
              <Button variant="ghost" size="sm" className="p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); handleDelete(e); }}>
                <Trash2 className="h-4 w-6" />
              </Button>
            )}
            {chattable && chatNode && InjectedChatButton ? (
              // Shared add-to-context button bound to this node — single
              // source of the node→context-item mapping.
              <InjectedChatButton
                node={chatNode}
                className="p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              />
            ) : chattable && onChat ? ( // Legacy callback affordance
              <Button variant="ghost" size="sm" className="p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); handleChat(); }}>
                <MessageSquareShare className="h-4 w-6" />
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

